import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, InputField } from 'src/components/ui'
import { Currency } from 'src/enums/Currency'
import { formatPrice } from 'src/helpers/formatPrice'
import { IOffer } from 'src/interfaces/IProperty'
import {
  useAcceptOfferMutation,
  useCounterOfferMutation,
  useDeclineOfferMutation,
  useGetPropertyOffersQuery,
  useGetUserQuery,
  useProposeOfferMutation,
  useUpdateOfferMutation,
} from 'src/redux/api'

interface IOfferBox {
  propertyId: string
  // The other party in this negotiation: the listing owner (buyer's view) or a
  // specific buyer (owner's view).
  counterpartId: string
  defaultCurrency: Currency
}

// the status colours of the design system (the old amber/green/stone ones
// read at 2.5–3:1 on white)
const statusClass: Record<IOffer['status'], string> = {
  PENDING: 'text-warning',
  ACCEPTED: 'text-success',
  DECLINED: 'text-danger',
  COUNTERED: 'text-ink-2',
  UPDATED: 'text-ink-2',
}

// Structured bargaining UI (roadmap 41 B2). Renders the caller's offer thread
// with one counterpart and the actions available on the latest offer: propose
// when nothing is open, or accept / decline / counter a pending offer aimed at
// the caller. The list query is cached per property, so many boxes on one page
// share a single request.
const OfferBox: FC<IOfferBox> = ({ propertyId, counterpartId, defaultCurrency }) => {
  const { t } = useTranslation()
  const { data: currentUser } = useGetUserQuery(undefined)
  const { data } = useGetPropertyOffersQuery(propertyId)
  const [propose, { isLoading: isProposing }] = useProposeOfferMutation()
  const [accept, { isLoading: isAccepting }] = useAcceptOfferMutation()
  const [decline, { isLoading: isDeclining }] = useDeclineOfferMutation()
  const [counter, { isLoading: isCountering }] = useCounterOfferMutation()
  const [update, { isLoading: isUpdating }] = useUpdateOfferMutation()

  const [mode, setMode] = useState<'idle' | 'propose' | 'counter' | 'edit'>('idle')
  const [amount, setAmount] = useState('')

  const meId = currentUser?.id
  const thread = useMemo(
    () =>
      (data?.items ?? []).filter(
        (o) =>
          (o.fromUserId === meId && o.toUserId === counterpartId) ||
          (o.fromUserId === counterpartId && o.toUserId === meId),
      ),
    [data, meId, counterpartId],
  )

  if (!meId || meId === counterpartId) return null

  const latest = thread[thread.length - 1]
  const activePending = latest?.status === 'PENDING' ? latest : null
  const iCanRespond = !!activePending && activePending.toUserId === meId
  const iAwait = !!activePending && activePending.fromUserId === meId
  const canPropose = !activePending
  const busy = isProposing || isAccepting || isDeclining || isCountering || isUpdating

  const submitAmount = async () => {
    const value = Number(amount)
    if (!value || value <= 0) return
    try {
      if (mode === 'counter' && activePending) {
        await counter({ id: activePending.id, amount: value }).unwrap()
      } else if (mode === 'edit' && activePending) {
        await update({ id: activePending.id, amount: value }).unwrap()
      } else {
        await propose({
          propertyId,
          amount: value,
          currency: defaultCurrency,
          toUserId: counterpartId,
        }).unwrap()
      }
      setAmount('')
      setMode('idle')
    } catch {
      // toastMiddleware surfaces the backend message
    }
  }

  return (
    <div className="flex flex-col gap-2 w-full pt-4 border-0 border-t border-solid border-ink/[0.08]">
      <h4 className="m-0 text-sm font-extrabold leading-5 text-ink">{t('offer.title')}</h4>

      {thread.length > 0 && (
        <ul className="flex flex-col gap-1 m-0 p-0 list-none">
          {thread.map((offer) => (
            <li
              key={offer.id}
              className="flex justify-between items-center gap-2 text-sm bg-page rounded-lg px-3 py-2"
            >
              <span className="text-ink">
                {offer.fromUserId === meId ? t('offer.you') : t('offer.them')}:{' '}
                <span className="font-black">{formatPrice(offer.amount, offer.currency)}</span>
              </span>
              <span className={`font-bold whitespace-nowrap ${statusClass[offer.status]}`}>
                {t(`offer.status_${offer.status.toLowerCase()}`)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {iAwait && mode !== 'edit' && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-warning">{t('offer.awaiting')}</span>
          <Button
            type="button"
            variant="outlined"
            size="sm"
            disabled={busy}
            onClick={() => {
              setMode('edit')
              setAmount(String(activePending!.amount))
            }}
          >
            {t('offer.edit')}
          </Button>
        </div>
      )}

      {iCanRespond && mode !== 'counter' && (
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            disabled={busy}
            className="flex-1"
            onClick={() => accept(activePending!.id)}
          >
            {t('offer.accept')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="danger"
            disabled={busy}
            className="flex-1"
            onClick={() => decline(activePending!.id)}
          >
            {t('offer.decline')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outlined"
            disabled={busy}
            className="flex-1"
            onClick={() => {
              setMode('counter')
              setAmount('')
            }}
          >
            {t('offer.counter')}
          </Button>
        </div>
      )}

      {canPropose && mode !== 'propose' && (
        <Button
          type="button"
          size="sm"
          variant="outlined"
          disabled={busy}
          onClick={() => {
            setMode('propose')
            setAmount('')
          }}
        >
          {t('offer.propose')}
        </Button>
      )}

      {(mode === 'propose' || mode === 'counter' || mode === 'edit') && (
        // the field on its own line, the two buttons under it: side by side
        // in the narrow seller card the field was left 74px wide
        <div className="flex flex-wrap gap-2">
          <InputField
            name="offerAmount"
            type="number"
            label={t('offer.yourPrice')}
            value={amount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
          />
          <Button
            type="button"
            className="flex-1"
            disabled={busy || !amount}
            onClick={submitAmount}
          >
            {t('offer.send')}
          </Button>
          <Button
            type="button"
            variant="neutral"
            className="flex-1"
            onClick={() => {
              setMode('idle')
              setAmount('')
            }}
          >
            {t('offer.cancel')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default OfferBox
